import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  ShirtIcon,
  Ruler,
  Palette,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import { getAdminRMAs, getAdminReturns } from '@/lib/api';
import { cn } from '@/lib/utils';

const REASON_LABELS = {
  'too_small': 'Troppo piccolo',
  'too_large': 'Troppo grande',
  'different_fit': 'Vestibilità diversa',
  'different_color': 'Colore diverso',
  'defect': 'Difetto',
  'changed_mind': 'Ripensamento',
  'other': 'Altro'
};

const SEVERITY_CONFIG = {
  high: { 
    color: 'bg-red-100 text-red-700 border-red-200', 
    icon: AlertTriangle,
    label: 'Alta priorità'
  },
  medium: { 
    color: 'bg-amber-100 text-amber-700 border-amber-200', 
    icon: AlertCircle,
    label: 'Media priorità'
  },
  low: { 
    color: 'bg-blue-100 text-blue-700 border-blue-200', 
    icon: TrendingDown,
    label: 'Bassa priorità'
  }
};

const AdminAnalisiPage = () => {
  const [rmas, setRMAs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const rmasData = await getAdminRMAs();
      setRMAs(rmasData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setIsLoading(false);
  };

  // Analyze products by return reasons
  const productAnalysis = useMemo(() => {
    const products = {};

    rmas.forEach(rma => {
      rma.items?.forEach(item => {
        const productKey = item.title;
        
        if (!products[productKey]) {
          products[productKey] = {
            title: item.title,
            variant_title: item.variant_title,
            totalReturns: 0,
            reasons: {},
            fitDetails: {},
            suggestions: []
          };
        }

        products[productKey].totalReturns++;
        
        // Track reasons
        const reason = item.q3_return_reason;
        if (reason) {
          products[productKey].reasons[reason] = (products[productKey].reasons[reason] || 0) + 1;
        }

        // Track fit details
        const fitDetails = item.q8_fit_detail || [];
        fitDetails.forEach(detail => {
          products[productKey].fitDetails[detail] = (products[productKey].fitDetails[detail] || 0) + 1;
        });
      });
    });

    // Generate suggestions for each product
    Object.values(products).forEach(product => {
      const suggestions = [];
      const totalReturns = product.totalReturns;
      
      // Analyze size issues
      const tooSmall = product.reasons['too_small'] || 0;
      const tooLarge = product.reasons['too_large'] || 0;
      const differentFit = product.reasons['different_fit'] || 0;
      const sizeIssues = tooSmall + tooLarge + differentFit;
      
      if (sizeIssues > 0) {
        const sizeIssuePercent = Math.round((sizeIssues / totalReturns) * 100);
        
        if (tooSmall > tooLarge) {
          suggestions.push({
            type: 'size',
            severity: sizeIssuePercent > 50 ? 'high' : 'medium',
            message: `${sizeIssuePercent}% dei resi per taglia troppo piccola`,
            action: 'Consiglia di prendere una taglia in più nella descrizione prodotto',
            icon: Ruler
          });
        } else if (tooLarge > tooSmall) {
          suggestions.push({
            type: 'size',
            severity: sizeIssuePercent > 50 ? 'high' : 'medium',
            message: `${sizeIssuePercent}% dei resi per taglia troppo grande`,
            action: 'Consiglia di prendere una taglia in meno nella descrizione prodotto',
            icon: Ruler
          });
        }
        
        if (differentFit > 0) {
          suggestions.push({
            type: 'fit',
            severity: differentFit > 2 ? 'high' : 'medium',
            message: `${differentFit} clienti hanno segnalato vestibilità diversa`,
            action: 'Aggiungi note dettagliate sulla vestibilità (aderente/regolare/ampio)',
            icon: ShirtIcon
          });
        }
      }

      // Analyze fit details
      const fitDetailMessages = [];
      Object.entries(product.fitDetails).forEach(([detail, count]) => {
        if (count >= 2) {
          const detailLabels = {
            'tight_chest': 'stretto sul petto',
            'tight_hips': 'stretto sui fianchi',
            'tight_waist': 'stretto in vita',
            'long_length': 'troppo lungo',
            'short_length': 'troppo corto',
            'wide_shoulders': 'largo sulle spalle',
            'narrow_shoulders': 'stretto sulle spalle'
          };
          fitDetailMessages.push(`${count} clienti: ${detailLabels[detail] || detail}`);
        }
      });
      
      if (fitDetailMessages.length > 0) {
        suggestions.push({
          type: 'fit_detail',
          severity: 'medium',
          message: 'Problemi specifici di vestibilità segnalati',
          action: fitDetailMessages.join('; '),
          icon: ShirtIcon
        });
      }

      // Analyze color issues
      const colorIssues = product.reasons['different_color'] || 0;
      if (colorIssues > 0) {
        suggestions.push({
          type: 'color',
          severity: colorIssues > 2 ? 'high' : 'low',
          message: `${colorIssues} resi per colore diverso dalle aspettative`,
          action: 'Migliora le foto prodotto o aggiungi nota sul colore reale',
          icon: Palette
        });
      }

      // Analyze defects
      const defects = product.reasons['defect'] || 0;
      if (defects > 0) {
        suggestions.push({
          type: 'quality',
          severity: defects > 1 ? 'high' : 'medium',
          message: `${defects} segnalazioni di difetto`,
          action: 'Verifica qualità del prodotto con il fornitore',
          icon: AlertTriangle
        });
      }

      product.suggestions = suggestions;
    });

    // Sort by total returns (most returned first)
    return Object.values(products)
      .filter(p => p.suggestions.length > 0)
      .sort((a, b) => b.totalReturns - a.totalReturns);
  }, [rmas]);

  // Overall stats
  const overallStats = useMemo(() => {
    const allReasons = {};
    rmas.forEach(rma => {
      rma.items?.forEach(item => {
        const reason = item.q3_return_reason;
        if (reason) {
          allReasons[reason] = (allReasons[reason] || 0) + 1;
        }
      });
    });

    return Object.entries(allReasons)
      .map(([reason, count]) => ({
        name: REASON_LABELS[reason] || reason,
        value: count,
        reason
      }))
      .sort((a, b) => b.value - a.value);
  }, [rmas]);

  // High priority products (more than 3 returns or high severity suggestions)
  const highPriorityProducts = useMemo(() => {
    return productAnalysis.filter(p => 
      p.totalReturns >= 3 || 
      p.suggestions.some(s => s.severity === 'high')
    );
  }, [productAnalysis]);

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-semibold text-gray-900">Analisi Prodotti</h1>
        <p className="text-gray-500 text-sm">Suggerimenti per migliorare le descrizioni basati sui feedback dei resi</p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{highPriorityProducts.length}</p>
              <p className="text-sm text-gray-500">Prodotti da rivedere</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Ruler className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">
                {Math.round(((overallStats.find(s => s.reason === 'too_small')?.value || 0) + 
                  (overallStats.find(s => s.reason === 'too_large')?.value || 0)) / 
                  Math.max(1, rmas.length) * 100)}%
              </p>
              <p className="text-sm text-gray-500">Problemi di taglia</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{rmas.length}</p>
              <p className="text-sm text-gray-500">RMA analizzati</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Reasons Chart */}
      {overallStats.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mb-8"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Distribuzione Motivi di Reso</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={overallStats} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#9ca3af" width={140} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {overallStats.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={
                        entry.reason === 'defect' ? '#ef4444' :
                        entry.reason === 'too_small' || entry.reason === 'too_large' ? '#f59e0b' :
                        entry.reason === 'different_color' ? '#8b5cf6' :
                        '#3b82f6'
                      } 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Product Suggestions */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Suggerimenti per Prodotto
        </h3>
      </div>

      {productAnalysis.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-12 border border-gray-200 text-center"
        >
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Tutto a posto!</h3>
          <p className="text-gray-500">
            Non ci sono suggerimenti per migliorare le descrizioni dei prodotti al momento.
            <br />Continua a monitorare i feedback dei clienti.
          </p>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {productAnalysis.map((product, idx) => (
            <motion.div
              key={product.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * idx }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              {/* Product Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">{product.title}</h4>
                    {product.variant_title && (
                      <p className="text-sm text-gray-500">{product.variant_title}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                      {product.totalReturns} {product.totalReturns === 1 ? 'reso' : 'resi'}
                    </span>
                    {product.suggestions.some(s => s.severity === 'high') && (
                      <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
                        Priorità alta
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Suggestions */}
              <div className="p-6 space-y-4">
                {product.suggestions.map((suggestion, sIdx) => {
                  const config = SEVERITY_CONFIG[suggestion.severity];
                  const Icon = suggestion.icon;
                  
                  return (
                    <div
                      key={sIdx}
                      className={cn(
                        "p-4 rounded-lg border",
                        config.color
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{suggestion.message}</p>
                          <p className="text-sm mt-1 opacity-80">
                            <strong>Azione suggerita:</strong> {suggestion.action}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAnalisiPage;
